/**
 * The traveler's selective silhouette (lifted from lantern-water's
 * `visibility.js` `createTravelerHint`): when most of the traveler's head and
 * torso is hidden behind scenery, an outline and a faint fill of its sprite
 * are drawn over what hides it, so it can be found; ordinary foot occlusion
 * behind a low rail never triggers it. Six probes in the vertex shader compare
 * the sprite's depth with the depth snapshot (no read back, no extra target,
 * no second render of the world); the overlay draws only where the scene is
 * nearer (`GreaterDepth`) into the lens's beauty target after the snapshot
 * (`LensOverlay`). A proxy (no sheet) draws no silhouette.
 */
import * as THREE from 'three';
import type { ProjectedActor } from '../character-shadows.ts';
import { sceneLook } from './constants.ts';

const vertexShader = `#include <packing>
uniform sampler2D spriteMap,sceneDepth;
uniform vec4 frame;uniform float flip;
uniform float cameraNear,cameraFar;
varying vec2 vUv;
varying float vHint;
vec2 atlas(vec2 uv){return vec2(frame.x+(flip<0.?1.-uv.x:uv.x)*frame.z,frame.y+(1.-uv.y)*frame.w);}
vec2 covered(vec2 uv){
  if(texture2D(spriteMap,atlas(uv)).a<.4)return vec2(0.);
  vec4 view=modelViewMatrix*vec4(uv-.5,0.,1.);
  vec4 clip=projectionMatrix*view;
  vec2 screen=clip.xy/clip.w*.5+.5;
  if(any(lessThan(screen,vec2(0.)))||any(greaterThan(screen,vec2(1.))))return vec2(0.);
  float depth=unpackRGBAToDepth(texture2D(sceneDepth,screen));
  float surface=-perspectiveDepthToViewZ(depth,cameraNear,cameraFar);
  return vec2(step(surface+${sceneLook.hint.margin.toFixed(3)},-view.z),1.);
}
void main(){
  vUv=uv;
  vec2 samples=covered(vec2(.44,.70))+covered(vec2(.56,.70))+covered(vec2(.44,.55))+covered(vec2(.56,.55))+covered(vec2(.44,.40))+covered(vec2(.56,.40));
  vHint=smoothstep(.34,.67,samples.x/max(samples.y,1.));
  gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);
  gl_Position.z-=.000002*gl_Position.w;
}`;

const fragmentShader = `uniform sampler2D spriteMap;
uniform vec4 frame;uniform float flip;uniform vec2 texel;
uniform vec3 edgeColor,fillColor;
varying vec2 vUv;
varying float vHint;
vec2 atlas(vec2 uv){return vec2(frame.x+(flip<0.?1.-uv.x:uv.x)*frame.z,frame.y+(1.-uv.y)*frame.w);}
void main(){
  if(vHint<.01)discard;
  float a=texture2D(spriteMap,atlas(vUv)).a;
  if(a<.4)discard;
  float inner=min(min(texture2D(spriteMap,atlas(vUv+vec2(texel.x,0.))).a,texture2D(spriteMap,atlas(vUv-vec2(texel.x,0.))).a),
                  min(texture2D(spriteMap,atlas(vUv+vec2(0.,texel.y))).a,texture2D(spriteMap,atlas(vUv-vec2(0.,texel.y))).a));
  float edge=1.-smoothstep(.3,.8,inner);
  gl_FragColor=vec4(mix(fillColor,edgeColor,edge),mix(.17,.85,edge)*vHint);
}`;

export type TravelerHint = {
  /** Points the silhouette at the traveler's cut-out for this frame (null hides it); the depth is the lens's snapshot. */
  set(actor: ProjectedActor | null, rotation: THREE.Quaternion, depth: THREE.Texture, camera: THREE.PerspectiveCamera): void;
  /** Draws it into the beauty target (a `LensOverlay`). */
  draw(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget, camera: THREE.PerspectiveCamera): void;
  /** Frees the plane's GPU copy (Off; it uploads again at Medium). */
  release(): void;
  dispose(): void;
};

export const createTravelerHint = (): TravelerHint => {
  const uniforms = {
    spriteMap: { value: null as THREE.Texture | null },
    sceneDepth: { value: null as THREE.Texture | null },
    frame: { value: new THREE.Vector4(0, 0, 1, 1) },
    flip: { value: 1 },
    texel: { value: new THREE.Vector2(1 / 48, 1 / 64) },
    cameraNear: { value: 0.5 },
    cameraFar: { value: 150 },
    edgeColor: { value: new THREE.Color(sceneLook.hint.edge) },
    fillColor: { value: new THREE.Color(sceneLook.hint.fill) },
  };
  const material = new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader, transparent: true, depthTest: true, depthWrite: false, depthFunc: THREE.GreaterDepth, side: THREE.DoubleSide, forceSinglePass: true });
  const geometry = new THREE.PlaneGeometry(1, 1);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  const overlay = new THREE.Scene();
  overlay.add(mesh);
  const local = new THREE.Matrix4();
  const at = new THREE.Vector3();
  const one = new THREE.Vector3(1, 1, 1);
  let shown = false;
  return {
    set(actor, rotation, depth, camera) {
      const c = actor?.cutout;
      shown = actor !== null && c !== undefined && c.texture !== null;
      if (actor === null || c === undefined || c.texture === null) return;
      uniforms.spriteMap.value = c.texture;
      uniforms.sceneDepth.value = depth;
      uniforms.frame.value.set(...c.rect);
      uniforms.flip.value = c.flip ? -1 : 1;
      // One texel of an M cell (48 × 64) in the atlas's UV units.
      uniforms.texel.value.set(1 / 48, 1 / 64);
      uniforms.cameraNear.value = camera.near;
      uniforms.cameraFar.value = camera.far;
      mesh.matrix.compose(at.set(actor.x, actor.y, actor.z), rotation, one).multiply(local.makeTranslation(c.width / 2 - c.pivotX, c.height / 2 - c.pivotY, 0)).multiply(local.makeScale(c.width, c.height, 1));
      mesh.matrixWorld.copy(mesh.matrix);
    },
    draw(renderer, target, camera) {
      if (!shown) return;
      const autoClear = renderer.autoClear;
      renderer.autoClear = false;
      renderer.setRenderTarget(target);
      try {
        renderer.render(overlay, camera);
      } finally {
        renderer.autoClear = autoClear;
      }
    },
    release() {
      geometry.dispose();
      material.dispose();
    },
    dispose() {
      material.dispose();
      geometry.dispose();
    },
  };
};
